import { z } from "zod";

const PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50] as const;
const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[1];

const PageSizeSchema = z.union(
  PAGE_SIZE_OPTIONS.map((size) => z.literal(size)) as [
    z.ZodLiteral<(typeof PAGE_SIZE_OPTIONS)[0]>,
    ...z.ZodLiteral<(typeof PAGE_SIZE_OPTIONS)[number]>[],
  ]
);

const PaginationStateSchema = z.object({
  pageIndex: z.number().int().min(0),
  pageSize: PageSizeSchema,
});

const PaginationMetaSchema = z.object({
  page: z.number().int().positive(),
  pageSize: PageSizeSchema,
  totalCount: z.number().int().min(0),
  totalPages: z.number().int().min(0),
});

const PaginationLinksSchema = z.object({
  next: z.string().optional(),
  current: z.string(),
  prev: z.string().optional(),
});

const PaginationSearchSchemaBase = z.object({
  limit: PageSizeSchema.default(DEFAULT_PAGE_SIZE).transform(String),
});

const PaginationOffsetSearchSchema = PaginationSearchSchemaBase.extend({
  offset: z.number().min(0).default(0).transform(String),
});

const PaginationPageSearchSchema = PaginationSearchSchemaBase.extend({
  page: z.number().min(1).transform(String),
});

const PaginatedResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    data: z.array(dataSchema),
    meta: PaginationMetaSchema,
  });

type PageSize = z.infer<typeof PageSizeSchema>;
type PaginationState = z.infer<typeof PaginationStateSchema>;
type PaginationMeta = z.infer<typeof PaginationMetaSchema>;
type PaginationSearchBase = z.input<typeof PaginationSearchSchemaBase>;
type PaginationOffsetSearch = z.input<typeof PaginationOffsetSearchSchema>;
type PaginationPageSearch = z.input<typeof PaginationPageSearchSchema>;
type PaginatedResponse<T> = {
  data: T[];
  meta: PaginationMeta;
};
type PaginationLinks = z.infer<typeof PaginationLinksSchema>;

export {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  PageSizeSchema,
  PaginationStateSchema,
  PaginationMetaSchema,
  PaginationOffsetSearchSchema,
  PaginationPageSearchSchema,
  PaginationSearchSchemaBase,
  PaginationLinksSchema,
  PaginatedResponseSchema,
};

export type {
  PageSize,
  PaginationState,
  PaginationMeta,
  PaginationSearchBase,
  PaginationOffsetSearch,
  PaginationPageSearch,
  PaginatedResponse,
  PaginationLinks,
};
